import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  Users, BookOpen, UserPlus, UserMinus, ArrowLeft, 
  Eye, Heart, MessageCircle, Calendar, Mail, Award,
  ExternalLink, Shield, Globe, Feather, PenLine, Flame
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";

const WriterProfilePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, api } = useAuth();
  const [writer, setWriter] = useState(null);
  const [stories, setStories] = useState([]);
  const [followers, setFollowers] = useState([]);
  const [following, setFollowing] = useState([]);
  const [isFollowing, setIsFollowing] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [writerRes, storiesRes, followersRes, followingRes] = await Promise.all([
          api.get(`/users/${id}?current_user_id=${user?.id || ''}`),
          api.get(`/stories?author_id=${id}`),
          api.get(`/users/${id}/followers`),
          api.get(`/users/${id}/following`)
        ]);
        
        setWriter(writerRes.data);
        setStories(storiesRes.data);
        setFollowers(followersRes.data);
        setFollowing(followingRes.data);
        setIsFollowing(writerRes.data.is_following || false);
      } catch (error) {
        console.error('Failed to load writer profile:', error);
        navigate('/discover');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, api, user, navigate]);

  const handleFollow = async () => {
    if (!user) {
      navigate('/login');
      return;
    }
    
    try {
      if (isFollowing) {
        await api.delete(`/users/${id}/follow`);
        setIsFollowing(false);
        setWriter(prev => ({ ...prev, follower_count: prev.follower_count - 1 }));
      } else {
        await api.post(`/users/${id}/follow`);
        setIsFollowing(true);
        setWriter(prev => ({ ...prev, follower_count: prev.follower_count + 1 }));
      }
    } catch (error) {
      console.error('Failed to toggle follow:', error);
    }
  };

  const totalViews = stories.reduce((sum, s) => sum + (s.view_count || 0), 0);
  const totalLikes = stories.reduce((sum, s) => sum + (s.like_count || 0), 0);

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-12">
        <Skeleton className="h-64 w-full mb-8" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (!writer) return null;

  const isOwnProfile = user?.id === writer.id;

  const avatarSrc = writer.avatar_url?.startsWith('/api')
    ? `${process.env.REACT_APP_BACKEND_URL}${writer.avatar_url}`
    : writer.avatar_url;

  return (
    <div className="min-h-screen" data-testid="writer-profile-page">
      {/* Portfolio Header - Full Width Banner */}
      <div className="bg-gradient-to-br from-brand-primary/10 via-brand-gold/5 to-brand-accent/10 py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link 
            to="/discover" 
            className="inline-flex items-center gap-2 text-text-muted hover:text-text-primary mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Discover
          </Link>

          <div className="flex flex-col md:flex-row items-start gap-8">
            {/* Avatar */}
            <Avatar className="w-32 h-32 border-4 border-white shadow-soft-md">
              <AvatarImage src={avatarSrc} alt={writer.display_name} />
              <AvatarFallback className="bg-brand-primary text-white text-4xl">
                {(writer.display_name || writer.username)?.[0]?.toUpperCase()}
              </AvatarFallback>
            </Avatar>
            
            {/* Profile Info */}
            <div className="flex-1">
              <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                <div>
                  <h1 className="font-serif text-4xl font-semibold text-text-primary mb-1">
                    {writer.display_name || writer.username}
                  </h1>
                  <p className="text-text-muted text-lg">@{writer.username}</p>
                  
                  {writer.bio && (
                    <p className="text-text-secondary mt-4 leading-relaxed max-w-2xl whitespace-pre-wrap">
                      {writer.bio}
                    </p>
                  )}

                  {writer.favorite_genres && writer.favorite_genres.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-4">
                      {writer.favorite_genres.map(genre => (
                        <Link 
                          key={genre}
                          to={`/stories?genre=${encodeURIComponent(genre)}`}
                          className="text-sm px-3 py-1 rounded-full bg-white/80 text-brand-primary hover:bg-white transition-colors"
                        >
                          {genre}
                        </Link>
                      ))}
                    </div>
                  )}

                  {writer.currently_working_on && (
                    <p className="text-sm text-text-muted mt-3 flex items-center gap-1.5" data-testid="currently-working-on">
                      <PenLine className="w-3.5 h-3.5 text-brand-accent" />
                      Currently working on: <span className="text-text-secondary italic">{writer.currently_working_on}</span>
                    </p>
                  )}

                  <div className="flex flex-wrap items-center gap-3 mt-3">
                    {writer.website_url && (
                      <a 
                        href={writer.website_url.startsWith('http') ? writer.website_url : `https://${writer.website_url}`}
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm text-brand-primary hover:text-brand-primary-hover flex items-center gap-1"
                        data-testid="writer-website-link"
                      >
                        <Globe className="w-3.5 h-3.5" />
                        Website
                      </a>
                    )}
                    {writer.social_links && Object.entries(writer.social_links).map(([platform, link]) => (
                      <a 
                        key={platform}
                        href={link.startsWith('http') ? link : `https://${link}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-brand-accent hover:text-brand-accent-hover flex items-center gap-1"
                      >
                        <ExternalLink className="w-3 h-3" />
                        {platform}
                      </a>
                    ))}
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  {!isOwnProfile && user && (
                    <Link to={`/messages?to=${writer.id}`}>
                      <Button variant="outline" className="btn-outline gap-2">
                        <Mail className="w-4 h-4" />
                        Message
                      </Button>
                    </Link>
                  )}
                  {!isOwnProfile && (
                    <Button
                      onClick={handleFollow}
                      className={isFollowing ? "btn-outline" : "btn-accent"}
                      data-testid="follow-button"
                    >
                      {isFollowing ? (
                        <>
                          <UserMinus className="w-4 h-4 mr-2" />
                          Following
                        </>
                      ) : (
                        <>
                          <UserPlus className="w-4 h-4 mr-2" />
                          Follow
                        </>
                      )}
                    </Button>
                  )}
                  {isOwnProfile && (
                    <Link to="/profile">
                      <Button variant="outline" className="btn-outline">
                        Edit Profile
                      </Button>
                    </Link>
                  )}
                </div>
              </div>

              {/* Stats Row */}
              <div className="flex flex-wrap items-center gap-8 mt-8 pt-6 border-t border-brand-primary/10">
                <div className="text-center">
                  <p className="font-semibold text-2xl text-text-primary">{stories.length}</p>
                  <p className="text-sm text-text-muted">Works</p>
                </div>
                <div className="text-center">
                  <p className="font-semibold text-2xl text-text-primary">{writer.follower_count || 0}</p>
                  <p className="text-sm text-text-muted">Followers</p>
                </div>
                <div className="text-center">
                  <p className="font-semibold text-2xl text-text-primary">{writer.following_count || 0}</p>
                  <p className="text-sm text-text-muted">Following</p>
                </div>
                <div className="text-center">
                  <p className="font-semibold text-2xl text-text-primary">{totalViews}</p>
                  <p className="text-sm text-text-muted">Total Views</p>
                </div>
                <div className="text-center">
                  <p className="font-semibold text-2xl text-text-primary">{totalLikes}</p>
                  <p className="text-sm text-text-muted">Total Likes</p>
                </div>
                {(writer.writing_streak > 0 || writer.longest_streak > 0) && (
                  <div className="text-center" data-testid="writer-streak">
                    <p className="font-semibold text-2xl text-text-primary flex items-center justify-center gap-1">
                      <Flame className={`w-5 h-5 ${writer.writing_streak >= 7 ? 'text-orange-500' : 'text-amber-500'}`} />
                      {writer.writing_streak || 0}
                    </p>
                    <p className="text-sm text-text-muted">Day Streak</p>
                  </div>
                )}
                <div className="flex items-center gap-1 text-sm text-text-muted ml-auto">
                  <Calendar className="w-4 h-4" />
                  Member since {new Date(writer.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bio Sections */}
      {(writer.how_i_got_here || writer.what_ive_done || writer.additional_bio) && (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-8" data-testid="writer-bio-sections">
          <div className="grid md:grid-cols-2 gap-6">
            {writer.how_i_got_here && (
              <div className="bg-white rounded-xl p-6 border border-[#E5E2DC]" data-testid="bio-how-i-got-here">
                <h3 className="font-serif text-lg font-semibold text-text-primary mb-3">How I Got Here</h3>
                <p className="text-text-secondary leading-relaxed whitespace-pre-wrap">{writer.how_i_got_here}</p>
              </div>
            )}
            {writer.what_ive_done && (
              <div className="bg-white rounded-xl p-6 border border-[#E5E2DC]" data-testid="bio-what-ive-done">
                <h3 className="font-serif text-lg font-semibold text-text-primary mb-3">What I've Done</h3>
                <p className="text-text-secondary leading-relaxed whitespace-pre-wrap">{writer.what_ive_done}</p>
              </div>
            )}
          </div>
          {writer.additional_bio && (
            <div className="mt-6 bg-white rounded-xl p-6 border border-[#E5E2DC]" data-testid="bio-additional">
              <h3 className="font-serif text-lg font-semibold text-text-primary mb-3">A Little More About Me</h3>
              <p className="text-text-secondary leading-relaxed whitespace-pre-wrap">{writer.additional_bio}</p>
            </div>
          )}
        </div>
      )}

      {/* Portfolio Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Tabs defaultValue="portfolio">
          <TabsList className="bg-surface-alt mb-8">
            <TabsTrigger value="portfolio" data-testid="tab-portfolio">
              <BookOpen className="w-4 h-4 mr-2" />
              Portfolio ({stories.length})
            </TabsTrigger>
            <TabsTrigger value="followers" data-testid="tab-followers">
              Followers ({followers.length})
            </TabsTrigger>
            <TabsTrigger value="following" data-testid="tab-following">
              Following ({following.length})
            </TabsTrigger>
          </TabsList>

          {/* Portfolio Tab */}
          <TabsContent value="portfolio">
            {stories.length === 0 ? (
              <div className="text-center py-16">
                <BookOpen className="w-16 h-16 text-text-muted mx-auto mb-4" />
                <h3 className="font-serif text-xl font-semibold text-text-primary mb-2">No published works yet</h3>
                <p className="text-text-secondary">
                  {isOwnProfile ? "Start writing to build your portfolio!" : "This writer hasn't published any works yet."}
                </p>
                {isOwnProfile && (
                  <Link to="/write" className="mt-4 inline-block">
                    <Button className="btn-accent">Start Writing</Button>
                  </Link>
                )}
              </div>
            ) : (
              <>
                {/* Portfolio Grid */}
                <div className="grid md:grid-cols-2 gap-6">
                  {stories.map((story) => (
                    <Link 
                      key={story.id} 
                      to={`/story/${story.id}`}
                      className="group block"
                      data-testid={`portfolio-story-${story.id}`}
                    >
                      <Card className="h-full bg-white border-brand-primary/10 overflow-hidden card-hover">
                        {story.cover_image && (
                          <div className="aspect-[16/9] overflow-hidden">
                            <img 
                              src={story.cover_image}
                              alt={story.title}
                              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                            />
                          </div>
                        )}
                        <CardContent className={`p-6 ${!story.cover_image ? 'pt-8' : ''}`}>
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-xs px-2 py-0.5 rounded-full bg-brand-primary/10 text-brand-primary">
                              {story.genre}
                            </span>
                            {story.challenge_id && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-brand-gold/20 text-brand-gold flex items-center gap-1">
                                <Award className="w-3 h-3" />
                                Challenge Entry
                              </span>
                            )}
                          </div>
                          <h3 className="font-serif text-xl font-semibold text-text-primary mb-2 group-hover:text-brand-accent transition-colors">
                            {story.title}
                          </h3>
                          {story.summary && (
                            <p className="text-text-secondary text-sm line-clamp-2 mb-4">
                              {story.summary}
                            </p>
                          )}
                          <div className="flex items-center justify-between pt-4 border-t border-brand-primary/10 text-sm text-text-muted">
                            <div className="flex items-center gap-4">
                              <span className="flex items-center gap-1">
                                <Eye className="w-4 h-4" /> {story.view_count}
                              </span>
                              <span className="flex items-center gap-1">
                                <Heart className="w-4 h-4" /> {story.like_count}
                              </span>
                            </div>
                            <span className="flex items-center gap-1 text-brand-accent opacity-0 group-hover:opacity-100 transition-opacity">
                              Read <ExternalLink className="w-3 h-3" />
                            </span>
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  ))}
                </div>

                {/* Ownership Notice */}
                <div className="mt-12 p-6 bg-surface-alt rounded-xl border border-brand-primary/10" data-testid="portfolio-ownership-notice">
                  <div className="flex items-start gap-4">
                    <Shield className="w-8 h-8 text-brand-primary flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-semibold text-text-primary mb-1">100% Writer Ownership &middot; Original Work Only</h4>
                      <p className="text-sm text-text-secondary">
                        All works in this portfolio are owned entirely by {writer.display_name || writer.username}. 
                        Collectively Write claims no rights to any content. Writers retain full copyright and 
                        may remove their work at any time. All writing on this platform must be original—no AI-generated content.
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}
          </TabsContent>

          {/* Followers Tab */}
          <TabsContent value="followers">
            {followers.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-12 h-12 text-text-muted mx-auto mb-4" />
                <p className="text-text-secondary">No followers yet</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {followers.map((follower) => (
                  <Link 
                    key={follower.id} 
                    to={`/writer/${follower.id}`}
                    data-testid={`follower-${follower.id}`}
                  >
                    <Card className="bg-white border-brand-primary/10 card-hover">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          <Avatar className="w-12 h-12">
                            <AvatarImage src={follower.avatar_url} />
                            <AvatarFallback className="bg-brand-primary text-white">
                              {(follower.display_name || follower.username)?.[0]?.toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium text-text-primary">
                              {follower.display_name || follower.username}
                            </p>
                            <p className="text-sm text-text-muted">@{follower.username}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Following Tab */}
          <TabsContent value="following">
            {following.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-12 h-12 text-text-muted mx-auto mb-4" />
                <p className="text-text-secondary">Not following anyone yet</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {following.map((followed) => (
                  <Link 
                    key={followed.id} 
                    to={`/writer/${followed.id}`}
                    data-testid={`following-${followed.id}`}
                  >
                    <Card className="bg-white border-brand-primary/10 card-hover">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          <Avatar className="w-12 h-12">
                            <AvatarImage src={followed.avatar_url} />
                            <AvatarFallback className="bg-brand-primary text-white">
                              {(followed.display_name || followed.username)?.[0]?.toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium text-text-primary">
                              {followed.display_name || followed.username}
                            </p>
                            <p className="text-sm text-text-muted">@{followed.username}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default WriterProfilePage;
